import React from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

const focusOn = (e) => {
  e.target.style.borderColor = "#E07A3A";
  e.target.style.boxShadow = "0 0 0 3px rgba(224, 122, 58, 0.1)";
};
const focusOff = (e) => {
  e.target.style.borderColor = "#E5E7EB";
  e.target.style.boxShadow = "none";
};

const baseInput =
  "w-full rounded-[10px] border border-[#E5E7EB] px-4 py-3 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150 disabled:cursor-not-allowed disabled:bg-[#F9FAFB] disabled:text-[#9CA3AF]";

const Label = ({ children, required }) => (
  <label className="mb-2 block text-sm font-medium text-[#374151]">
    {children}
    {required && <span className="ml-0.5 text-[#E07A3A]">*</span>}
  </label>
);

const Hint = ({ hint }) =>
  hint ? <p className="mt-1.5 text-xs text-[#9CA3AF]">{hint}</p> : null;

export const TextField = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  disabled = false,
  hint,
  maxLength,
  className = "",
  inputRef,
  autoFocus = false,
}) => (
  <div className={className}>
    {label && <Label required={required}>{label}</Label>}
    <input
      ref={inputRef}
      autoFocus={autoFocus}
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      className={baseInput}
      onFocus={focusOn}
      onBlur={focusOff}
    />
    <Hint hint={hint} />
  </div>
);

export const TextAreaField = ({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  hint,
  rows = 3,
  className = "",
}) => (
  <div className={className}>
    {label && <Label required={required}>{label}</Label>}
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      rows={rows}
      className={`${baseInput} resize-none`}
      onFocus={focusOn}
      onBlur={focusOff}
    />
    <Hint hint={hint} />
  </div>
);

export const SelectField = ({
  label,
  value,
  onChange,
  options = [],
  placeholder: placeholderProp,
  required = false,
  disabled = false,
  hint,
  className = "",
}) => {
  const { t } = useTranslation();
  const placeholder =
    placeholderProp === undefined
      ? t("admin.manufacturing.form.selectPlaceholder")
      : placeholderProp;
  return (
    <div className={className}>
      {label && <Label required={required}>{label}</Label>}
      <div className="relative">
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={`${baseInput} cursor-pointer appearance-none pr-10`}
          onFocus={focusOn}
          onBlur={focusOff}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
        />
      </div>
      <Hint hint={hint} />
    </div>
  );
};
